import { Component, computed, DestroyRef, inject } from '@angular/core';
import { catchError, finalize, map, of, tap } from 'rxjs';
import { CaFullFormComponent } from '../components/ca-full-form/ca-full-form';
import { ProfileDataService } from 'src/app/shared/store/features/profile-data/profile-data.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CalistDataService } from 'src/app/shared/store/features/calist-data/calist-data.service';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { CryptotokenDataService } from 'src/app/shared/store/features/cryptotoken-data/cryptotoken-data.service';
import { PageService } from 'src/app/shared/page/service/page.service';
import { Page } from 'src/app/shared/page/page.config';
import { CreateCaFullDto } from 'src/app/shared/services/api/dto/create-ca-full-dto';
import { ApiService } from 'src/app/shared/services/api/api.service';
import { ToastrService } from 'src/app/shared/services/toastr/toastr-service';
import { extractErrorMessage } from 'src/app/shared/util/extract-error-message';
import { LangService } from 'src/app/shared/services/lang/lang.service';

@Component({
  selector: 'app-provision-operational-ca-page',
  imports: [CaFullFormComponent, SpinnerComponent],
  templateUrl: './provision-operational-ca-page.html',
  styleUrl: './provision-operational-ca-page.scss',
})
export class ProvisionOperationalCaPageComponent {
  private destroyRef = inject(DestroyRef);
  private apiService = inject(ApiService);
  private langService = inject(LangService);
  private labels = this.langService.lang.features.provisionOperationalCa;
  private toastrService = inject(ToastrService);
  private pageService = inject(PageService);

  private profileDataService = inject(ProfileDataService);
  profiles = toSignal(
    this.profileDataService.data$.pipe(map(data => data || [])),
    { initialValue: [] },
  );

  private caDataService = inject(CalistDataService);
  cas = toSignal(this.caDataService.data$.pipe(map(data => data || [])), {
    initialValue: [],
  });

  private cryptotokenDataService = inject(CryptotokenDataService);
  cryptotokens = toSignal(
    this.cryptotokenDataService.data$.pipe(map(data => data || [])),
    { initialValue: [] },
  );

  private areProfilesLoading = toSignal(this.profileDataService.isLoading$, {
    initialValue: false,
  });
  private areCasLoading = toSignal(this.caDataService.isLoading$, {
    initialValue: false,
  });
  private areCryptotokensLoading = toSignal(
    this.cryptotokenDataService.isLoading$,
    { initialValue: false },
  );

  isLoading = computed(() => {
    const areProfilesLoading = this.areProfilesLoading();
    const areCasLoading = this.areCasLoading();
    const areCryptotokensLoading = this.areCryptotokensLoading();
    return areProfilesLoading || areCasLoading || areCryptotokensLoading;
  });

  isCreating = false;

  constructor() {
    this.profileDataService.ensure();
    this.caDataService.ensure();
    this.cryptotokenDataService.ensure();
  }

  onCancel() {
    this.pageService.goToPage(Page.CaList);
  }

  onCreate(dto: CreateCaFullDto) {
    this.isCreating = true;
    this.apiService
      .provisionOperationalCa(dto)
      .pipe(
        tap(() => {
          this.caDataService.clear();
          this.cryptotokenDataService.clear();
          this.profileDataService.clear();
          this.pageService.goToPage(Page.CaList);
          this.toastrService.success(this.labels.successMessage);
        }),
        catchError(error => {
          console.error('Error provisioning operational CA', error);
          this.toastrService.error(
            extractErrorMessage(error) || this.labels.defaultError,
          );
          return of();
        }),
        finalize(() => {
          this.isCreating = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
